/* eslint-disable react/require-default-props */
import React from 'react';
import { useSpeech, type SpeechPlaybackControl } from 'hooks/useSpeech';
import { ProfileState } from 'store/modules/profile_store/types';
import { ApplicationState } from 'store';
import { useSelector } from 'react-redux';
import { Button } from './styles';

interface SpeechButtonViewProps {
  speech: SpeechPlaybackControl;
  text?: string;
  getText?: () => string;
  variant?: 'chat' | 'modal' | 'modalOutline';
  idleIcon?: string;
  className?: string;
}

function SpeechButtonView({
  speech,
  text = '',
  getText,
  variant = 'modal',
  idleIcon,
  className,
}: SpeechButtonViewProps) {
  const { speak, stop, isSpeaking, isLoading } = speech;

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  if (!profile?.alfa_tester) return null;

  const handleClick = async () => {
    if (isSpeaking) {
      stop();
      return;
    }

    const content = getText ? getText() : text;
    if (!content.trim()) return;

    await speak(content);
  };

  const getLabel = () => {
    if (isLoading) return 'Gerando áudio...';
    if (isSpeaking) return 'Parar áudio';

    if (variant === 'chat') return 'Ouvir resposta';
    if (variant === 'modalOutline') return 'Ler em voz alta';
    return 'Ouvir resumo completo';
  };

  const getIcon = () => {
    if (isSpeaking) return 'stop_circle';
    if (variant === 'modalOutline') return idleIcon || 'volume_up';
    return idleIcon || 'play_arrow';
  };

  const loadingBars = (
    <div className="audio-loading-bars" aria-hidden>
      <span />
      <span />
      <span />
    </div>
  );

  if (variant === 'modalOutline') {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={isLoading}
        title={getLabel()}
        className={`btn bidding-summary-header-action-btn ${className ?? ''}`}
      >
        {isLoading ? (
          <span
            className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: 22, height: 22 }}
            aria-hidden
          >
            {loadingBars}
          </span>
        ) : (
          <span className="material-icons">{getIcon()}</span>
        )}
        <span className="bidding-summary-header-action-btn-label">{getLabel()}</span>
      </button>
    );
  }

  return (
    <Button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      $variant={variant === 'chat' ? 'chat' : 'modal'}
      title={getLabel()}
      className={className}
    >
      {variant === 'chat' ? (
        <span className="speech-chat-icon-slot" aria-hidden>
          {isLoading ? loadingBars : <span className="material-icons">{getIcon()}</span>}
        </span>
      ) : (
        <>
          {isLoading ? (
            <span className="d-inline-flex align-items-center justify-content-center" aria-hidden>
              {loadingBars}
            </span>
          ) : (
            <span className="material-icons">{getIcon()}</span>
          )}

          {variant === 'modal' && getLabel()}
        </>
      )}
    </Button>
  );
}

interface Props {
  text?: string;
  getText?: () => string;
  variant?: 'chat' | 'modal' | 'modalOutline';
  idleIcon?: string;
  className?: string;
  speechPlayback?: SpeechPlaybackControl;
}

function SpeechButtonUncontrolled(props: Omit<Props, 'speechPlayback'>) {
  const speech = useSpeech();
  return <SpeechButtonView {...props} speech={speech} />;
}

export function SpeechButton({ speechPlayback, ...props }: Props) {
  if (speechPlayback) {
    return <SpeechButtonView {...props} speech={speechPlayback} />;
  }
  return <SpeechButtonUncontrolled {...props} />;
}
